import { useQueryClient } from '@tanstack/react-query'
import { Navigate, createFileRoute } from '@tanstack/react-router'

import { Page } from '@/components/page'
import { LoadingPage } from '@/components/skeletons'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { IdentityCard } from '@/features/parts/detail/IdentityCard'
import { MovementsCard } from '@/features/parts/detail/Movements'
import { PartActions, PartBreadcrumb, PartTags } from '@/features/parts/detail/PartHeader'
import { PartSections } from '@/features/parts/detail/Sections'
import { StockSection } from '@/features/parts/detail/StockSection'
import { usePart, type PartDetail } from '@/features/parts/api'
import { invalidateStock } from '@/features/stock/api'
import { useSettings } from '@/features/settings/api'
import { atTheShelf } from '@/features/shelf/posture'
import { useMediaQuery } from '@/lib/use-media-query'

/** `?lot=` is what a scanned lot label (`/go/{code}`) points at. */
interface PartSearch {
  lot?: string
}

export const Route = createFileRoute('/parts/$partId/')({
  validateSearch: (search: Record<string, unknown>): PartSearch => ({
    lot: typeof search.lot === 'string' && search.lot ? search.lot : undefined,
  }),
  component: PartDetailPage,
})

function PartDetailPage() {
  const { partId } = Route.useParams()
  const { lot } = Route.useSearch()
  const part = usePart(partId)

  // A scanned lot label on a phone at the shelf opens that lot's drawer (D38).
  const drawer = part.data?.lots.find((row) => row.id === lot)?.location_id
  if (lot && drawer && atTheShelf()) {
    return (
      <Navigate
        to="/shelf/drawer/$locationId"
        params={{ locationId: drawer }}
        search={{ lot }}
        replace
      />
    )
  }

  if (part.isPending) {
    return (
      <Page title="Part">
        <LoadingPage label="Loading the part" />
      </Page>
    )
  }

  if (part.isError || !part.data) {
    return (
      <Page title="Part">
        <Alert variant="destructive">
          <AlertTitle>Could not load this part</AlertTitle>
          <AlertDescription>
            {part.error instanceof Error ? part.error.message : 'It may have been deleted.'}
          </AlertDescription>
        </Alert>
      </Page>
    )
  }

  return <PartView part={part.data} />
}

/** The part page (screen 2d): stock and movements on the left, identity on the right. */
function PartView({ part }: { part: PartDetail }) {
  const { lot } = Route.useSearch()
  const queryClient = useQueryClient()
  const settings = useSettings()
  const currency = settings.data?.base_currency ?? 'EUR'
  // Beside the stock on a wide screen, between the stock and the sections otherwise.
  const wide = useMediaQuery('(min-width: 1280px)')

  const refresh = () => invalidateStock(queryClient)

  return (
    <Page
      title={part.name}
      breadcrumb={<PartBreadcrumb part={part} />}
      description={<PartTags part={part} />}
      actions={<PartActions part={part} />}
    >
      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-6">
          <StockSection part={part} highlightLot={lot} onChanged={refresh} />
          <MovementsCard partId={part.id} unit={part.unit_symbol ?? null} />
          {!wide && <IdentityCard part={part} currency={currency} />}
          <PartSections part={part} currency={currency} onChanged={refresh} />
        </div>
        {wide && (
          <div className="min-w-0">
            <div className="sticky top-20">
              <IdentityCard part={part} currency={currency} />
            </div>
          </div>
        )}
      </div>
    </Page>
  )
}
